// Bridges the read-after-write gap on the "transcription complete" notification.
//
// The backend publishes a "transcribe success" notification the moment a recording's
// transcript is saved, and useChats' transcriptionCompleted handler immediately
// refetches the chat (api.getChat) to paint it. But that refetch can be served before
// the just-committed write is readable — the same read-replica race the codebase
// already guards against in parseRecording's `skipRefresh` path ("...could race the
// write on a read replica and clobber it with stale content..."). When that happens
// the refetch returns the chat WITHOUT the new transcript, so the open note stays
// blank until the user manually reloads. Bigger recordings take longer to become
// readable, so long dictations fail most reliably — which is exactly the segmented /
// drain-assembled case (15-60 min sessions).
//
// This polls api.getChat a few times with a short escalating backoff until the chat's
// transcript actually advances past what is already on screen, then lets the caller
// surface it. It is bounded and FAILS OPEN: if the content never advances within the
// budget it returns the freshest response it got — never worse than the single fetch
// it replaces. The caller only invokes it when the just-transcribed chat is the one
// open, so there is zero added latency for background transcriptions or when the read
// is not actually lagging.

import { parseTranscript } from "./transcriptParser";

const DEFAULT_DELAYS_MS = [400, 800, 1500, 2500, 4000];
const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A stable fingerprint of the transcript content a chat is currently showing. Each
// recording appends to local:transcript and bumps the cumulative
// local:transcriptTotalDuration, so a change in EITHER field means the new transcript
// has landed. Null-safe so an empty/!loaded chat compares cleanly.
export function chatTranscriptSignal(chat) {
  const cv = (chat && chat.contextVariables) || {};
  const transcript = cv["local:transcript"];
  const duration = cv["local:transcriptTotalDuration"];
  return JSON.stringify([transcript == null ? null : transcript, duration == null ? null : duration]);
}

// Every append to local:transcript — audio AND manual — shares one bracketed-
// header format (see transcriptParser.js), distinguished only by `info`:
//   "Audio Note"                      — a real recording
//   "Manual Input"                    — free text from the Human Inputs tab's
//                                        "Manual Transcription" box (no section
//                                        prefix: appendManualTranscript is
//                                        called with label=undefined there)
//   "<Section Title> Manual Input"    — a section-title PREFIXED entry. Written
//                                        when a provider edits an ALREADY-
//                                        GENERATED section directly (see
//                                        ChatNoteSectionBodyDefault.js,
//                                        ChatNoteSectionBodyPlanOfCare.js) or
//                                        when a voice-edit AI rewrite is
//                                        captured (useChats.captureVoiceEditAsHumanInput).
//                                        This is an edit-history record of
//                                        content the note ALREADY reflects, not
//                                        new source material — regenerating
//                                        would not pick up anything new from it.
// Only the first two are "new material a note should be regenerated from".
const isNoteRelevantEntry = (entry) => entry.info === "Audio Note" || entry.info === "Manual Input";

// A fingerprint of only the transcript content that should drive "does this
// note need regeneration" — real audio plus genuinely-new free-text manual
// input, WITHOUT section-edit / voice-edit "Manual Input" entries (see
// isNoteRelevantEntry). Editing an already-generated section previously wrote
// into the same local:transcript string chatTranscriptSignal fingerprints,
// which made every section edit falsely flag the note as stale even though
// nothing new existed to regenerate from (2026-07-22 incident: "Plan of Care
// Summary Manual Input" edits triggering the indicator). Duration is kept
// as-is — it only changes on a real audio save (useChats.js), never on any
// manual-input path — so it needs no filtering.
export function noteRelevantTranscriptSignal(chat) {
  const cv = (chat && chat.contextVariables) || {};
  const transcript = cv["local:transcript"];
  const duration = cv["local:transcriptTotalDuration"];
  const relevant = transcript
    ? parseTranscript(transcript)
        .filter(isNoteRelevantEntry)
        .map((e) => e.originalEntry)
    : [];
  return JSON.stringify([relevant, duration == null ? null : duration]);
}

// True when a generated note no longer reflects the chat's current transcript —
// i.e. audio was recorded, or new manual-transcription text was added, AFTER
// the note was last generated. generate() (useChats) stamps
// local:noteGeneratedSignal with noteRelevantTranscriptSignal at generation
// time; if the live signal no longer matches, the note is stale and should be
// regenerated to pick up the change. Editing an already-generated section (or
// a voice-edit capture) does NOT count — see noteRelevantTranscriptSignal.
//
// Fails closed (returns false) when there is no note yet, or no stamp at all
// (notes generated before this feature shipped) — so it never nags on legacy
// notes or on a chat that was never generated. Only main-transcript drift is
// considered; ER/wound phase notes have their own _on / _transcript_on staleness.
export function isNoteStale(chat) {
  const cv = (chat && chat.contextVariables) || {};
  if (!cv["local:noteDesign"]) return false;
  const stamped = cv["local:noteGeneratedSignal"];
  if (stamped == null) return false;
  return noteRelevantTranscriptSignal(chat) !== stamped;
}

// True when the chat holds ANY transcript content — the main var or any
// per-phase variant (local:<phase>_transcript). Used as a last-line guard
// before telling the native app a recording "parsed" (which deletes its local
// audio copy): a "success" completion whose chat has NO transcript at all
// means the save never became durable (e.g. the run's DB commit failed AFTER
// the success notification fired — observed in prod 2026-07-01). Deliberately
// coarse: it cannot catch a failed APPEND to an already-non-empty transcript,
// only the catastrophic blank case, and that asymmetry is safe — a false
// "error" keeps audio and offers a retry; a false "parsed" destroys audio.
export function chatHasAnyTranscript(chat) {
  const cv = (chat && chat.contextVariables) || {};
  for (const key in cv) {
    // The backend writes "local:transcript" or "local:<phase>_transcript";
    // phase names are caller-defined, so match ANY local:*_transcript rather
    // than assuming a charset — a miss here false-errors a real success.
    if (key !== "local:transcript" && !(key.startsWith("local:") && key.endsWith("_transcript"))) continue;
    const value = cv[key];
    if (typeof value === "string" && value.length > 0) return true;
  }
  return false;
}

// True when the chat holds MEANINGFUL transcript content — text beyond the
// bracketed attribution headers ("[07/21/2026 @ 12:57:34 by Muina, Barbara]",
// "[Manual Input ... by ...]") that every save/append writes even when the
// recording contained no speech. chatHasAnyTranscript (above) deliberately
// stays coarse — it guards native-audio destruction, where a false "empty"
// is safe. THIS check guards note GENERATION, where a false "has content"
// is the incident case: a header-only transcript passed length>0 and a full
// boilerplate note was generated from zero words (2026-07-21, chat
// 16:15835149). Same key matching as chatHasAnyTranscript; strips every
// bracketed block and answers whether any non-whitespace text remains.
export function chatHasMeaningfulTranscript(chat) {
  const cv = (chat && chat.contextVariables) || {};
  for (const key in cv) {
    if (key !== "local:transcript" && !(key.startsWith("local:") && key.endsWith("_transcript"))) continue;
    const value = cv[key];
    if (typeof value !== "string" || value.length === 0) continue;
    if (value.replace(/\[[^\]]*\]/g, "").trim().length > 0) return true;
  }
  return false;
}

// getChat: (chatId) => Promise<{ data: chat }>  (pass a thunk so `this`-binding never
//          matters: (id) => api.getChat(id))
// beforeChat: the chat object already on screen (the pre-transcript snapshot).
// Returns { res, extraFetches, advanced }.
export async function refetchChatUntilTranscriptAdvances(getChat, chatId, beforeChat, opts = {}) {
  const { maxAttempts = DEFAULT_DELAYS_MS.length, delays = DEFAULT_DELAYS_MS, sleep = defaultSleep } = opts;
  const before = chatTranscriptSignal(beforeChat);

  // First fetch is intentionally UNGUARDED: a hard failure here surfaces exactly as
  // it did before this retry wrapper existed (the handler used a single bare getChat).
  let res = await getChat(chatId);
  let extraFetches = 0;
  while (extraFetches < maxAttempts && chatTranscriptSignal(res && res.data) === before) {
    await sleep(delays[Math.min(extraFetches, delays.length - 1)]);
    extraFetches += 1;
    try {
      res = await getChat(chatId);
    } catch (e) {
      // A transient failure on a RETRY must not discard the response we already
      // hold — stop polling and surface the last good one (fail open). Worst case
      // equals the old single-fetch behavior; it is never worse.
      console.warn("refetchChatUntilTranscriptAdvances: retry fetch failed, using last response", e);
      break;
    }
  }

  return { res, extraFetches, advanced: chatTranscriptSignal(res && res.data) !== before };
}
